import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { ClientModule, PlanOffer, PlanOverrun, PlatformClientDetail } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { ClientFeaturesCard } from './ClientFeaturesCard'
import { ClientLifecycleCard } from './ClientLifecycleCard'
import { ClientInvoicesCard } from './ClientInvoicesCard'
import { ClientGrowthCard } from './ClientGrowthCard'
import { nomFormule } from './formules'
import { DateLisible, Nombre } from '../components/Nombre'
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionItem,
  DescriptionList,
  PageHeader,
  SelectField,
  Stack,
  TextField,
  useToast,
} from '../components/ui'
import type { SelectOption } from '../components/ui'

/**
 * La fiche d'un client : son entreprise, son abonnement, ses activités, son usage.
 *
 * CE QUE CET ÉCRAN DOIT DIRE, ET QUI N'EST PAS DÉCORATIF. Quatre faits ont été établis en
 * lisant le code, pas supposés, et chacun a coûté une vérification. Les afficher ne suffit
 * pas : il faut les NOMMER, parce que chacun est une chose qu'un super-administrateur
 * croirait fausse s'il se fiait à son intuition.
 *
 * 1. IL Y A DEUX LEVIERS SUR UN CLIENT, ET ILS NE FONT PAS LA MÊME CHOSE. Désactiver
 *    l'entreprise (`core.organization.is_active`) mord dans `core.find_user_for_sign_in` :
 *    plus personne n'ouvre de session. Suspendre l'abonnement (`saas.subscription.status`)
 *    mord dans `SubscriptionGuard`, qui n'accepte que `trial`, `active` et `past_due` : le
 *    client se connecte, LIT TOUT, et ne peut plus rien CRÉER. Les deux boutons vivent sur
 *    cette page ; sans un mot d'explication, on se trompera de levier — soit on ferme la
 *    porte en croyant relancer, soit l'inverse.
 *
 * 2. LE DÉCLASSEMENT S'ANNONCE EN CHIFFRES AVANT DE CONFIRMER. Un client à cinq
 *    exploitations peut passer à une formule qui en autorise une : ça passe, c'est décidé,
 *    parce que refuser obligerait à supprimer d'abord les données d'un client qui paie
 *    moins. Mais il doit le lire avant. Les chiffres viennent CALCULÉS de l'API, par
 *    `PlanDowngrade.Overruns` qui vit dans le domaine et porte ses tests : cet écran n'en
 *    refait aucun, sans quoi il en existerait une seconde version que rien n'éprouve.
 *
 * 3. LE MOTIF EST OBLIGATOIRE pour suspendre, résilier et fermer une activité — et le champ
 *    est donc DANS la confirmation. `ck_audit_module_change_reason` et
 *    `ck_subscription_cancellation` mordraient de toute façon, mais après le clic, avec un
 *    23514 qu'on ne sait pas afficher. Échouer après avoir fait confirmer serait grossier.
 *
 * 4. SEUL `crop` GARDE DES ROUTES, et le champ `guardsRoutes` de chaque module le dit,
 *    module par module. `RequireModule` n'est posé que sur les exploitations, parcelles,
 *    interventions et assolement, toutes pour `crop`. Fermer « Arboriculture » ou
 *    « Élevage » ne change aujourd'hui que le menu, parce que ces activités n'existent pas
 *    encore. Laisser croire l'inverse serait exactement le mensonge que ce dépôt traque.
 *
 * 5. LE PLAFOND D'UTILISATEURS N'EST PAS APPLIQUÉ. `ISubscriptionGuard` n'expose que
 *    `EnsureFarmSlotAsync` et `EnsurePlotSlotAsync` ; `CreateUser` n'appelle aucune garde.
 *    Un client au plan Essentiel, limité à trois utilisateurs, peut en créer trois cents. Le
 *    chiffre est affiché parce qu'il renseigne — mais AVEC la mention qu'il n'est pas tenu.
 *    Un chiffre montré comme une limite alors qu'il n'en est pas une est précisément ce que
 *    ce dépôt reproche au code ; l'écrire ici coûte une ligne et supprime le mensonge. Ce
 *    lot ne POSE PAS la garde manquante : c'est un changement de comportement client, qui se
 *    décide pour lui-même.
 */
export function ClientDetailPage() {
  const { t, language, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()
  const { id } = useParams<{ id: string }>()

  const token = session?.token ?? ''
  const clientId = Number(id)

  const [fiche, setFiche] = useState<PlatformClientDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [enCours, setEnCours] = useState(false)

  // L'acte en attente de confirmation, ou nul. Garder l'acte entier — et non un simple
  // drapeau — permet à la boîte de nommer ce qu'elle va faire et de porter son champ.
  const [acte, setActe] = useState<Acte | null>(null)
  const [motif, setMotif] = useState('')

  // La formule visée et le nombre de mois vivent HORS de la boîte : ils se choisissent
  // dans la section abonnement, et l'avertissement chiffré se lit avant même d'ouvrir la
  // confirmation. Un avertissement qui n'apparaîtrait qu'au moment du clic arriverait trop
  // tard pour peser sur le choix.
  const [planVise, setPlanVise] = useState('')
  const [mois, setMois] = useState('12')

  const charger = useCallback(() => {
    if (token === '' || !Number.isFinite(clientId)) return

    setLoading(true)

    api
      .getPlatformClient(token, clientId)
      .then((rendu) => {
        setFiche(rendu)
        setPlanVise((actuel) => (actuel === '' ? (rendu.subscription?.planCode ?? rendu.plans[0]?.code ?? '') : actuel))
      })
      .catch((cause: unknown) => notify(describeError(cause), 'erreur'))
      .finally(() => setLoading(false))
  }, [token, clientId, notify, describeError])

  useEffect(charger, [charger])

  const ouvrir = (prochain: Acte) => {
    setMotif('')
    setActe(prochain)
  }

  const confirmer = async () => {
    if (acte === null || fiche === null) return

    const motifNet = motif.trim()

    // La garde du bouton : le champ est requis, et le bouton reste désactivé sans lui. Ce
    // contrôle-ci est le second verrou, pour le cas où la touche Entrée court-circuiterait.
    if (acte.motifRequis && motifNet === '') return

    setEnCours(true)

    try {
      const message = await executer(acte, fiche.organizationId, token, motifNet === '' ? null : motifNet)
      notify(t(message), 'succes')
      setActe(null)
      charger()
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnCours(false)
    }
  }

  if (loading) {
    return <p className="text-texte-doux">{t('common.loading')}</p>
  }

  if (fiche === null) {
    return (
      <Stack gap={4}>
        <Link className="btn ghost" to="/platform">
          {t('platform.client.back')}
        </Link>
        <Alert tone="erreur">{t('platform.client_not_found')}</Alert>
      </Stack>
    )
  }

  const abonnement = fiche.subscription
  const offreVisee = fiche.plans.find((p) => p.code === planVise) ?? null
  const moisDemandes = Number(mois)

  return (
    <Stack gap={4}>
      <PageHeader
        title={fiche.legalName}
        description={t('platform.clients.description')}
        actions={
          <Link className="btn ghost" to="/platform">
            {t('platform.client.back')}
          </Link>
        }
      />

      {/* LE FAIT QUI COMMANDE TOUT L'ÉCRAN, écrit en premier et une fois pour toutes. Les
          deux boutons qu'il décrit sont plus bas, dans deux sections différentes ; sans ce
          paragraphe, rien ne dirait qu'ils ne sont pas deux façons de faire la même chose. */}
      <Alert tone="info" title={t('platform.client.levers.title')}>
        <p className="m-0 mb-2">{t('platform.client.levers.organization')}</p>
        <p className="m-0">{t('platform.client.levers.subscription')}</p>
      </Alert>

      {/* ----------------------------------------------------------------- 1. L'entreprise */}
      <Card title={t('platform.client.company')}>
        <Stack gap={4}>
          <DescriptionList>
            <DescriptionItem label={t('platform.clients.legalName')}>{fiche.legalName}</DescriptionItem>
            <DescriptionItem label={t('platform.client.orgType')}>{fiche.orgType ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.client.taxId')}>{fiche.taxId ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.client.city')}>{fiche.city ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.clients.governorate')}>{fiche.governorate ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.client.phone')}>{fiche.phone ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.clients.email')}>{fiche.email ?? '—'}</DescriptionItem>
            <DescriptionItem label={t('platform.clients.createdAt')}>
              <DateLisible valeur={fiche.createdAt} />
            </DescriptionItem>
            <DescriptionItem label={t('platform.clients.status')}>
              <Badge tone={fiche.isActive ? 'succes' : 'avertissement'}>
                {fiche.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            </DescriptionItem>
          </DescriptionList>

          <div className="flex flex-wrap gap-2">
            <Button
              variant={fiche.isActive ? 'danger' : 'secondaire'}
              onClick={() =>
                ouvrir({
                  genre: 'activation',
                  actif: !fiche.isActive,
                  titre: fiche.isActive ? 'platform.clients.deactivate.title' : 'platform.clients.activate.title',
                  message: fiche.isActive
                    ? 'platform.clients.deactivate.message'
                    : 'platform.clients.activate.message',
                  motifRequis: false,
                })
              }
            >
              {fiche.isActive ? t('common.deactivate') : t('common.activate')}
            </Button>
          </div>
        </Stack>
      </Card>

      {/* ----------------------------------------------------------------- 2. L'abonnement */}
      <Card title={t('platform.client.subscription')}>
        {abonnement === null ? (
          <Stack gap={4}>
            <Alert tone="avertissement">{t('platform.client.noSubscription')}</Alert>

            {/* LE SEUL CLIENT SANS ISSUE : aucune ligne d'abonnement. Un abonnement suspendu
                ou résilié s'affiche dans l'autre branche, avec « Réactiver » ; celui-ci n'a
                rien à réactiver. La formule présélectionnée est la première du catalogue. */}
            <div className="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_auto] md:items-end">
              <SelectField
                label={t('platform.clients.plan')}
                value={planVise}
                options={optionsFormules(fiche.plans, (p) => nomFormule(language, p))}
                onChange={setPlanVise}
              />
              <Button
                variant="secondaire"
                disabled={planVise === ''}
                onClick={() =>
                  ouvrir({
                    genre: 'attribution',
                    planCode: planVise,
                    titre: 'platform.client.grant.title',
                    message: 'platform.client.grant.message',
                    motifRequis: false,
                  })
                }
              >
                {t('platform.client.grant')}
              </Button>
            </div>
          </Stack>
        ) : (
          <Stack gap={4}>
            <DescriptionList>
              <DescriptionItem label={t('platform.clients.plan')}>
                {abonnement.planName ?? abonnement.planCode ?? '—'}
              </DescriptionItem>
              <DescriptionItem label={t('platform.clients.status')}>
                <Badge tone={abonnement.status === 'active' ? 'succes' : 'avertissement'}>
                  {t(`platform.subscription.${abonnement.status}` as never)}
                </Badge>
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.price')}>
                <Nombre valeur={abonnement.price} decimales={3} unite={abonnement.currencyCode ?? undefined} />
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.startedOn')}>
                <DateLisible valeur={abonnement.startedOn} />
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.periodEnd')}>
                <DateLisible valeur={abonnement.currentPeriodEnd} />
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.endedOn')}>
                <DateLisible valeur={abonnement.endedOn} />
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.autoRenew')}>
                {abonnement.autoRenew ? t('common.yes') : t('common.no')}
              </DescriptionItem>
              <DescriptionItem label={t('platform.client.cancellationReason')}>
                {abonnement.cancellationReason ?? '—'}
              </DescriptionItem>
            </DescriptionList>

            <div className="flex flex-wrap gap-2">
              {abonnement.status === 'active' ? (
                <Button
                  variant="danger"
                  onClick={() =>
                    ouvrir({
                      genre: 'statut',
                      statut: 'suspended',
                      titre: 'platform.client.suspend.title',
                      message: 'platform.client.suspend.message',
                      motifRequis: true,
                    })
                  }
                >
                  {t('platform.client.suspend')}
                </Button>
              ) : (
                <Button
                  variant="secondaire"
                  onClick={() =>
                    ouvrir({
                      genre: 'statut',
                      statut: 'active',
                      titre: 'platform.client.resume.title',
                      message: 'platform.client.resume.message',
                      motifRequis: false,
                    })
                  }
                >
                  {t('platform.client.resume')}
                </Button>
              )}

              <Button
                variant="danger"
                onClick={() =>
                  ouvrir({
                    genre: 'statut',
                    statut: 'cancelled',
                    titre: 'platform.client.terminate.title',
                    message: 'platform.client.terminate.message',
                    motifRequis: true,
                  })
                }
              >
                {t('platform.client.terminate')}
              </Button>
            </div>

            {/* Le changement de formule et l'avertissement qui l'accompagne. */}
            <div className="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_auto] md:items-end">
              <SelectField
                label={t('platform.client.changePlan')}
                value={planVise}
                options={optionsFormules(fiche.plans, (p) => nomFormule(language, p))}
                onChange={setPlanVise}
              />
              <Button
                variant="secondaire"
                disabled={planVise === '' || planVise === abonnement.planCode}
                onClick={() =>
                  ouvrir({
                    genre: 'formule',
                    planCode: planVise,
                    titre: 'platform.client.changePlan.title',
                    message: 'platform.client.downgrade.intro',
                    motifRequis: false,
                  })
                }
              >
                {t('platform.client.changePlan')}
              </Button>
            </div>

            {offreVisee !== null && offreVisee.code !== abonnement.planCode && (
              <AvertissementDeclassement offre={offreVisee} />
            )}

            {/* Prolonger. */}
            <div className="grid gap-3 md:grid-cols-[minmax(12rem,1fr)_auto] md:items-end">
              <TextField
                label={t('platform.client.months')}
                type="number"
                value={mois}
                onChange={setMois}
                hint={t('platform.client.extend.message')}
              />
              <Button
                variant="secondaire"
                disabled={!Number.isInteger(moisDemandes) || moisDemandes <= 0}
                onClick={() =>
                  ouvrir({
                    genre: 'prolongation',
                    mois: moisDemandes,
                    titre: 'platform.client.extend.title',
                    message: 'platform.client.extend.message',
                    motifRequis: false,
                  })
                }
              >
                {t('platform.client.extend')}
              </Button>
            </div>
          </Stack>
        )}
      </Card>

      {/* ------------------------------------------------------- 2 bis. Le cycle de vie */}
      {/* Ce qui arrivera tout seul (migration 0077) et ce qui est programmé. La clé suit
          l'abonnement : un geste de la carte au-dessus — réactiver, changer de formule —
          change ce que celle-ci doit montrer. */}
      <ClientLifecycleCard
        key={`${fiche.organizationId}-${abonnement?.status ?? ''}-${abonnement?.planCode ?? ''}-${abonnement?.currentPeriodEnd ?? ''}`}
        organizationId={fiche.organizationId}
        plans={fiche.plans}
        onChange={charger}
      />

      {/* ------------------------------------------------------- 2 ter. Les factures */}
      {/* Migration 0079 : ce qui a été facturé à ce client, et ce qui reste dû. Payer une
          facture de renouvellement réactive l'abonnement — la carte du dessus le montrera
          au prochain chargement de la fiche. */}
      <ClientInvoicesCard organizationId={fiche.organizationId} />

      {/* ------------------------------------------------ 2 quater. Options et remise */}
      {/* Migration 0081 : les plafonds effectifs (formule + options) et la remise en cours. */}
      <ClientGrowthCard organizationId={fiche.organizationId} />

      {/* -------------------------------------------------------------------- 3. Les modules */}
      <Card title={t('platform.client.modules')}>
        <Stack gap={3}>
          <p className="m-0 text-[0.85rem] text-texte-doux">{t('platform.client.modules.description')}</p>

          {fiche.modules.length === 0 ? (
            <p className="m-0 text-texte-doux">{t('platform.clients.empty')}</p>
          ) : (
            fiche.modules.map((m) => (
              <LigneModule
                key={m.moduleCode}
                module={m}
                onBascule={() =>
                  ouvrir({
                    genre: 'module',
                    moduleCode: m.moduleCode,
                    ouvert: !m.isEnabled,
                    titre: m.isEnabled ? 'platform.client.module.close.title' : 'platform.client.module.open.title',
                    // Fermer dit ce que la fermeture fait VRAIMENT pour CE module : refuser
                    // des écrans, ou seulement retirer une entrée de menu.
                    message: m.isEnabled
                      ? m.guardsRoutes
                        ? 'platform.client.module.guards'
                        : 'platform.client.module.cosmetic'
                      : 'platform.client.module.open.message',
                    motifRequis: m.isEnabled,
                  })
                }
              />
            ))
          )}
        </Stack>
      </Card>

      {/* ------------------------------------------------------ 3 bis. Les fonctionnalités */}
      {/* La clé suit la formule et le statut : changer de formule change ce que le client a,
          et la carte doit relire plutôt que d'afficher l'état d'avant. */}
      <ClientFeaturesCard
        key={`${fiche.organizationId}-${abonnement?.planCode ?? ''}-${abonnement?.status ?? ''}`}
        organizationId={fiche.organizationId}
      />

      {/* ---------------------------------------------------------------------- 4. L'usage */}
      <Card title={t('platform.client.usage')}>
        <Stack gap={3}>
          <p className="m-0 text-[0.85rem] text-texte-doux">{t('platform.client.usage.description')}</p>

          {abonnement === null ? (
            <Alert tone="avertissement">{t('platform.client.usage.noPlan')}</Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-bordure text-start text-texte-doux">
                    <th className="py-2 text-start font-medium">{t('platform.client.usage.limit')}</th>
                    <th className="py-2 text-end font-medium">{t('platform.client.usage.used')}</th>
                    <th className="py-2 text-end font-medium">{t('platform.client.usage.limit')}</th>
                  </tr>
                </thead>
                <tbody>
                  <LigneUsage
                    libelle={t('platform.client.limit.farms')}
                    consomme={fiche.usage.farmCount}
                    plafond={abonnement.maxFarms}
                    decimales={0}
                    sansLimite={t('platform.client.usage.unlimited')}
                  />
                  <LigneUsage
                    libelle={t('platform.client.limit.plots')}
                    consomme={fiche.usage.plotCount}
                    plafond={abonnement.maxPlots}
                    decimales={0}
                    sansLimite={t('platform.client.usage.unlimited')}
                  />
                  <LigneUsage
                    libelle={t('platform.client.limit.area')}
                    consomme={fiche.usage.totalAreaHectares}
                    plafond={abonnement.maxAreaHectares}
                    decimales={2}
                    unite="ha"
                    sansLimite={t('platform.client.usage.unlimited')}
                  />
                  <LigneUsage
                    libelle={t('platform.client.limit.users')}
                    consomme={fiche.usage.userCount}
                    plafond={abonnement.maxUsers}
                    decimales={0}
                    sansLimite={t('platform.client.usage.unlimited')}
                  />
                </tbody>
              </table>
            </div>
          )}

          {/* LA LIGNE QUI SUPPRIME LE MENSONGE. Le chiffre des utilisateurs est juste ; ce
              qui serait faux, c'est de le présenter comme une limite. CreateUser n'appelle
              aucune garde, et ce lot ne la pose pas — c'est un changement de comportement
              client, qui se décide pour lui-même. En attendant, la fiche le dit. */}
          <Alert tone="avertissement" title={t('platform.client.limit.users')}>
            {t('platform.client.usage.usersNotEnforced')}
          </Alert>
        </Stack>
      </Card>

      {/* La confirmation, qui porte son champ de motif quand la base en exige un. */}
      {acte !== null && (
        <BoiteDeConfirmation
          titre={t(acte.titre)}
          message={t(acte.message)}
          motifRequis={acte.motifRequis}
          motif={motif}
          onMotif={setMotif}
          occupe={enCours}
          onConfirmer={() => void confirmer()}
          onAnnuler={() => setActe(null)}
        >
          {/* L'avertissement chiffré est REPRIS dans la boîte, et pas seulement au-dessus :
              c'est le dernier écran avant l'acte, et c'est là qu'on lit vraiment. */}
          {acte.genre === 'formule' && offreVisee !== null && <AvertissementDeclassement offre={offreVisee} />}
        </BoiteDeConfirmation>
      )}
    </Stack>
  )
}

/**
 * L'acte qu'on s'apprête à confirmer.
 *
 * `motifRequis` VIENT DE LA RÈGLE, PAS DU GOÛT : `ck_audit_module_change_reason` exige un
 * motif pour `closed` et `suspended`, `ck_subscription_cancellation` pour la résiliation.
 * Le porter ici met le champ dans la boîte au lieu de laisser la base refuser après coup.
 */
type Acte =
  | { genre: 'activation'; actif: boolean; titre: Cle; message: Cle; motifRequis: boolean }
  | { genre: 'statut'; statut: 'active' | 'suspended' | 'cancelled'; titre: Cle; message: Cle; motifRequis: boolean }
  | { genre: 'formule'; planCode: string; titre: Cle; message: Cle; motifRequis: boolean }
  | { genre: 'attribution'; planCode: string; titre: Cle; message: Cle; motifRequis: boolean }
  | { genre: 'prolongation'; mois: number; titre: Cle; message: Cle; motifRequis: boolean }
  | { genre: 'module'; moduleCode: string; ouvert: boolean; titre: Cle; message: Cle; motifRequis: boolean }

/** Une clé du dictionnaire, portée telle quelle jusqu'à l'appel de `t`. */
type Cle = Parameters<ReturnType<typeof useI18n>['t']>[0]

/** Exécute l'acte et rend la clé du message de succès. */
async function executer(acte: Acte, clientId: number, token: string, motif: string | null): Promise<Cle> {
  switch (acte.genre) {
    case 'activation':
      await api.setPlatformClientActive(token, clientId, acte.actif)
      return acte.actif ? 'platform.clients.activated' : 'platform.clients.deactivated'

    case 'statut': {
      await api.setPlatformSubscriptionStatus(token, clientId, acte.statut, motif)
      return acte.statut === 'suspended'
        ? 'platform.client.suspended'
        : acte.statut === 'cancelled'
          ? 'platform.client.terminated'
          : 'platform.client.resumed'
    }

    case 'formule':
      await api.changePlatformClientPlan(token, clientId, acte.planCode, motif)
      return 'platform.client.planChanged'

    case 'attribution':
      await api.grantPlatformSubscription(token, clientId, acte.planCode, motif)
      return 'platform.client.granted'

    case 'prolongation':
      await api.extendPlatformSubscription(token, clientId, acte.mois)
      return 'platform.client.extended'

    case 'module': {
      const rendu = await api.setPlatformClientModule(token, clientId, acte.moduleCode, acte.ouvert, motif)

      // La base rend « rien n'a changé » quand le module était déjà dans cet état : le dire
      // vaut mieux qu'annoncer une ouverture qui n'a pas eu lieu.
      if (!rendu.changed) return 'platform.client.module.unchanged'

      return acte.ouvert ? 'platform.client.module.opened' : 'platform.client.module.closed'
    }
  }
}

/** Les formules, ordonnées comme l'API les rend : du moins cher au plus cher. */
function optionsFormules(plans: PlanOffer[], libelle: (plan: PlanOffer) => string): SelectOption[] {
  return plans.map((p) => ({ value: p.code, label: libelle(p) }))
}

/**
 * L'avertissement chiffré du déclassement.
 *
 * AUCUN CALCUL ICI. Les trois nombres de chaque ligne — ce que le client a, ce que la
 * formule autorise, l'écart — arrivent de l'API, où `PlanDowngrade.Overruns` les produit et
 * où huit tests les protègent, dont celui qui distingue un plafond NUL (« sans limite ») de
 * zéro. Les recalculer ici en donnerait une seconde version, que rien n'éprouverait.
 *
 * CHAQUE NOMBRE PASSE PAR <Nombre>. En arabe, le paragraphe se lit de droite à gauche et les
 * chiffres de gauche à droite : un nombre interpolé dans une phrase traduite s'y retourne.
 * C'est aussi pourquoi la phrase est composée de morceaux traduits séparément plutôt que
 * d'une chaîne à trous.
 */
function AvertissementDeclassement({ offre }: { offre: PlanOffer }) {
  const { t } = useI18n()

  if (offre.overruns.length === 0) {
    return <Alert tone="succes">{t('platform.client.downgrade.none')}</Alert>
  }

  return (
    <Alert tone="avertissement" title={t('platform.client.downgrade.title')}>
      <p className="m-0 mb-2">{t('platform.client.downgrade.intro')}</p>
      <ul className="m-0 ps-5">
        {offre.overruns.map((o) => (
          <li key={o.limit}>
            <LigneDepassement depassement={o} />
          </li>
        ))}
      </ul>
    </Alert>
  )
}

function LigneDepassement({ depassement }: { depassement: PlanOverrun }) {
  const { t } = useI18n()

  // La surface est fractionnaire ; les trois autres plafonds comptent des entiers.
  const decimales = depassement.limit === 'area' ? 2 : 0
  const unite = depassement.limit === 'area' ? 'ha' : undefined

  return (
    <span>
      <strong>{t(`platform.client.limit.${depassement.limit}` as never)}</strong>
      {' : '}
      {t('platform.client.downgrade.has')} <Nombre valeur={depassement.current} decimales={decimales} unite={unite} />
      {', '}
      {t('platform.client.downgrade.allows')} <Nombre valeur={depassement.allowed} decimales={decimales} unite={unite} />
      {!depassement.isEnforced && (
        <em className="text-texte-doux"> — {t('platform.client.downgrade.notEnforced')}</em>
      )}
    </span>
  )
}

/** Une ligne du tableau « usage face aux plafonds ». */
function LigneUsage({
  libelle,
  consomme,
  plafond,
  decimales,
  unite,
  sansLimite,
}: {
  libelle: string
  consomme: number
  /** NUL signifie « sans limite », jamais zéro : le mot s'écrit, le chiffre ne s'invente pas. */
  plafond: number | null
  decimales: number
  unite?: string
  sansLimite: string
}) {
  return (
    <tr className="border-b border-bordure/50">
      <td className="py-2">{libelle}</td>
      <td className="py-2 text-end">
        <Nombre valeur={consomme} decimales={decimales} unite={unite} />
      </td>
      <td className="py-2 text-end">
        {plafond === null ? (
          <span className="text-texte-doux">{sansLimite}</span>
        ) : (
          <Nombre valeur={plafond} decimales={decimales} unite={unite} />
        )}
      </td>
    </tr>
  )
}

/** Une activité et son état, avec ce que la fermer ferait vraiment. */
function LigneModule({ module, onBascule }: { module: ClientModule; onBascule: () => void }) {
  const { t } = useI18n()

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-app border border-bordure p-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{t(`module.${module.moduleCode}` as never)}</span>
          <Badge tone={module.isEnabled ? 'succes' : 'avertissement'}>
            {module.isEnabled ? t('common.active') : t('common.inactive')}
          </Badge>
        </div>

        {/* LA VÉRITÉ SUR CE QUE FERME CE MODULE, par module et pas en général. Seul `crop`
            garde des routes aujourd'hui ; l'annoncer autrement ferait croire à une coupure
            qui n'aurait pas lieu. */}
        <p className="m-0 mt-1 text-[0.8rem] text-texte-doux">
          {module.guardsRoutes ? t('platform.client.module.guards') : t('platform.client.module.cosmetic')}
        </p>

        {module.openedAt !== null && (
          <p className="m-0 text-[0.8rem] text-texte-doux">
            {t('platform.client.module.openedAt')} <DateLisible valeur={module.openedAt} />
          </p>
        )}
        {module.closedAt !== null && (
          <p className="m-0 text-[0.8rem] text-texte-doux">
            {t('platform.client.module.closedAt')} <DateLisible valeur={module.closedAt} />
          </p>
        )}
      </div>

      <Button variant={module.isEnabled ? 'danger' : 'secondaire'} onClick={onBascule}>
        {module.isEnabled ? t('platform.client.module.close') : t('platform.client.module.open')}
      </Button>
    </div>
  )
}

/**
 * Confirmation qui porte un CHAMP, et pas seulement un message.
 *
 * `ConfirmDialog` partagé ne prend qu'une chaîne : il ne peut ni recevoir le champ de motif
 * ni afficher l'avertissement chiffré. Plutôt que d'élargir un composant utilisé par tout le
 * produit pour un besoin propre à cette page, la boîte vit ici — même structure, même
 * comportement au clavier, un contenu en plus.
 *
 * LE BOUTON RESTE DÉSACTIVÉ TANT QUE LE MOTIF MANQUE, quand la base en exige un. C'est la
 * différence entre demander avant et échouer après.
 */
function BoiteDeConfirmation({
  titre,
  message,
  motifRequis,
  motif,
  onMotif,
  occupe,
  onConfirmer,
  onAnnuler,
  children,
}: {
  titre: string
  message: string
  motifRequis: boolean
  motif: string
  onMotif: (valeur: string) => void
  occupe: boolean
  onConfirmer: () => void
  onAnnuler: () => void
  children?: ReactNode
}) {
  const { t } = useI18n()

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnnuler()
    }

    window.addEventListener('keydown', surTouche)
    return () => window.removeEventListener('keydown', surTouche)
  }, [onAnnuler])

  const manque = motifRequis && motif.trim() === ''

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={titre}
    >
      <div className="my-auto w-full max-w-lg rounded-app border border-bordure bg-carte p-5 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold">{titre}</h2>
        <p className="mb-4 text-sm text-texte-doux">{message}</p>

        {children}

        {motifRequis && (
          <div className="mb-4 mt-4">
            <TextField
              label={t('platform.client.reason')}
              value={motif}
              onChange={onMotif}
              required
              hint={t('platform.client.reasonRequired')}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn ghost" onClick={onAnnuler} disabled={occupe}>
            {t('common.cancel')}
          </button>
          <Button variant="danger" onClick={onConfirmer} disabled={occupe || manque}>
            {t('common.confirm')}
          </Button>
        </div>
      </div>
    </div>
  )
}
